import { z } from 'zod';
import { daySchema } from './common';

export const mealSlotSchema = z.enum(['breakfast', 'lunch', 'dinner', 'snack']);

export const mealInput = z.object({
  clientIdempotencyKey: z.string().uuid(),
  eatenOn: daySchema,
  slot: mealSlotSchema,
  description: z.string().max(1000).nullable().optional(),
  kcal: z.number().int().min(0).max(20000).nullable().optional(),
  proteinG: z.number().min(0).max(2000).nullable().optional(),
  carbsG: z.number().min(0).max(2000).nullable().optional(),
  fatG: z.number().min(0).max(2000).nullable().optional(),
});

export const mealOutput = z.object({
  id: z.string(),
  eatenOn: z.string(),
  slot: mealSlotSchema,
  description: z.string().nullable(),
  kcal: z.number().nullable(),
  proteinG: z.number().nullable(),
  carbsG: z.number().nullable(),
  fatG: z.number().nullable(),
  photoUrls: z.array(z.string()),
  trainerComment: z.string().nullable(),
  trainerLikedAt: z.string().nullable(),
});

export const dayNutritionOutput = z.object({
  day: z.string(),
  meals: z.array(mealOutput),
  totals: z.object({
    kcal: z.number(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
  }),
  waterMl: z.number(),
});

/**
 * Odgovor `POST /v1/nutrition/meals`.
 *
 * `duplicate: true` je USPJEH, ne greška — server je isti `clientIdempotencyKey`
 * već primio i vraća id postojećeg obroka (201 pri prvom upisu, 200 pri
 * ponovljenom slanju). Bez toga bi offline retry stvorio drugi obrok.
 */
export const mealCreatedOutput = z.object({
  id: z.string(),
  duplicate: z.boolean(),
});

/**
 * `GET /v1/nutrition/history` — dnevnik unatrag, sažet po danu.
 * Ruta uzima zadnjih 200 obroka i grupira ih po `eatenOn`, najnoviji dan prvi.
 */
export const nutritionHistoryOutput = z.object({
  days: z.array(
    z.object({
      day: z.string(),
      count: z.number(),
      kcal: z.number(),
    }),
  ),
});

export const waterOutput = z.object({ ok: z.literal(true), ml: z.number().int().nonnegative() });

export const waterInput = z.object({
  loggedOn: daySchema,
  /** Apsolutna vrijednost za dan, ne inkrement — inače offline retry udvostruči unos. */
  ml: z.number().int().min(0).max(20000),
});

export type MealInput = z.infer<typeof mealInput>;
export type MealOutput = z.infer<typeof mealOutput>;
export type MealSlot = z.infer<typeof mealSlotSchema>;
export type MealCreatedOutput = z.infer<typeof mealCreatedOutput>;
export type NutritionHistoryOutput = z.infer<typeof nutritionHistoryOutput>;
export type DayNutritionOutput = z.infer<typeof dayNutritionOutput>;

/**
 * Potvrda fotografije obroka. Zapis nastaje TEK ovdje, kad objekt stvarno
 * postoji u R2 — isti obrazac kao fotografije napretka. Bez toga bi pali
 * prijenos ostavio red koji pokazuje na nepostojeći objekt.
 */
export const mealPhotoConfirmInput = z.object({ key: z.string().min(1) });
export const mealPhotoConfirmedOutput = z.object({ photoId: z.string() });

export type MealPhotoConfirmInput = z.infer<typeof mealPhotoConfirmInput>;

/**
 * Trenerova povratna informacija uz obrok.
 *
 * Dva odvojena polja jer su dvije radnje: „vidio sam i u redu je" (`liked`) i
 * pisani komentar. Izostavljeno polje se ne dira — tako trener može dati palac
 * bez brisanja ranijeg komentara, i obrnuto. Prazan komentar briše postojeći.
 */
export const mealFeedbackInput = z.object({
  comment: z.string().max(1000).nullable().optional(),
  liked: z.boolean().optional(),
});

export type MealFeedbackInput = z.infer<typeof mealFeedbackInput>;

/**
 * Obrok koji klijent cesto upisuje — prijedlog umjesto ponovnog racunanja.
 *
 * Postoji zato sto baza namirnica ne rjesava gotova i slozena jela: za „sarmu"
 * u bazi ili nema unosa ili ih ima dvadeset razlicitih. Klijent ju je jednom
 * upisao, i to je najtocniji podatak koji imamo.
 */
export const frequentMealRow = z.object({
  description: z.string(),
  slot: mealSlotSchema,
  kcal: z.number().nullable(),
  proteinG: z.number().nullable(),
  carbsG: z.number().nullable(),
  fatG: z.number().nullable(),
  timesLogged: z.number().int(),
  lastEatenOn: z.string(),
});

export const frequentMealsOutput = z.object({ items: z.array(frequentMealRow) });
export type FrequentMealRow = z.infer<typeof frequentMealRow>;

/**
 * Trenerov predlozak obroka.
 *
 * Nastavak `frequentMealRow`: ondje brojke dolaze iz klijentovih ranijih unosa,
 * ovdje ih je odredio trener. Klijent koji ne zna izracunati makronutrijente
 * dobiva gotov unos, a trener kontrolu nad time sto se nudi.
 *
 * `slot` je neobavezan — shake ili vocka nisu vezani uz doba dana.
 */
export const mealTemplateRow = z.object({
  id: z.string(),
  name: z.string(),
  slot: mealSlotSchema.nullable(),
  kcal: z.number().nullable(),
  proteinG: z.number().nullable(),
  carbsG: z.number().nullable(),
  fatG: z.number().nullable(),
  note: z.string().nullable(),
});

export const mealTemplatesOutput = z.object({ items: z.array(mealTemplateRow) });
export type MealTemplateRow = z.infer<typeof mealTemplateRow>;

/**
 * Brojke su neobavezne: trener smije ponuditi „Zobene s bananom" bez ijednog
 * broja i dopuniti ga poslije. Prazan naziv nema smisla, pa je jedini uvjet.
 */
export const mealTemplateInput = z.object({
  name: z.string().trim().min(1).max(120),
  slot: mealSlotSchema.nullable().optional(),
  kcal: z.number().int().min(0).max(10_000).nullable().optional(),
  proteinG: z.number().min(0).max(1_000).nullable().optional(),
  carbsG: z.number().min(0).max(1_000).nullable().optional(),
  fatG: z.number().min(0).max(1_000).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});

export type MealTemplateInput = z.infer<typeof mealTemplateInput>;
